@if (visible()) {
  <div
    class="palette-backdrop"
    (click)="onBackdropClick($event)">
    <div class="palette-container" (click)="$event.stopPropagation()">
      <div class="palette-resizer" appDrawerResize="form-palette-drawer-width" [minWidth]="640"></div>
      <div class="palette-header">
        <div class="palette-tabs">
          <button
            type="button"
            class="palette-tab"
            [class.active]="activeTab === 'question'"
            (click)="selectTab('question')">
            Question
          </button>
          <button
            type="button"
            class="palette-tab"
            [class.active]="activeTab === 'structural'"
            (click)="selectTab('structural')">
            Structural Elements
          </button>
          <button
            type="button"
            class="palette-tab"
            [class.active]="activeTab === 'existing'"
            (click)="selectTab('existing')">
            Existing Form Elements
          </button>
        </div>
        <button
          type="button"
          class="palette-close"
          (click)="close()"
          aria-label="Close">
          <i class="pi pi-times"></i>
        </button>
      </div>

      <!-- Search and Filter Controls for Existing Elements - Full Width -->
      @if (activeTab === 'existing') {
        <div class="existing-elements-controls">
          <div class="search-filter-container">
            <input
              type="text"
              class="search-input"
              [(ngModel)]="searchText"
              placeholder="Search by label..."
              aria-label="Search existing form elements">

            <div class="type-filter-container">
              @for (typeOption of availableQuestionTypes; track typeOption.value) {
                <label class="type-filter-radio">
                  <input
                    type="radio"
                    name="typeFilter"
                    [value]="typeOption.value === 'all' ? 'all' : typeOption.value"
                    [checked]="selectedTypeFilter === typeOption.value"
                    (change)="selectedTypeFilter = typeOption.value"
                    [attr.aria-label]="'Filter by ' + typeOption.label">
                  <span class="radio-label">{{ typeOption.label }}</span>
                </label>
              }
            </div>
          </div>
        </div>
      }

      <div class="palette-content">
        <div class="palette-sidebar">
          <div class="element-list">
            <!-- Loading state for existing elements -->
            @if (activeTab === 'existing' && (loadingExistingElements$ | async)) {
              <div class="element-list-loading">
                <p>Loading existing form elements...</p>
              </div>
            }

            <!-- Error state for existing elements -->
            @if (activeTab === 'existing' && (existingElementsError$ | async); as error) {
              <div class="element-list-error">
                <p>Error loading elements: {{ error }}</p>
              </div>
            }

            <!-- Empty state for existing elements -->
            @if (activeTab === 'existing' && !(loadingExistingElements$ | async) && !(existingElementsError$ | async) && filteredExistingElements.length === 0 && existingElements.length === 0) {
              <div class="element-list-empty">
                <p>No existing form elements found.</p>
              </div>
            }

            <!-- No results after filtering -->
            @if (activeTab === 'existing' && !(loadingExistingElements$ | async) && !(existingElementsError$ | async) && filteredExistingElements.length === 0 && existingElements.length > 0) {
              <div class="element-list-empty">
                <p>No elements match your search criteria.</p>
              </div>
            }

            <!-- Element list -->
            @for (element of currentElements; track element.id) {
              <button
                type="button"
                class="element-item"
                [class.selected]="selectedElement?.id === element.id"
                [class.existing-element]="element.type === 'existing'"
                (click)="selectElementForPreview(element)">
                @if (!element.iconType || element.iconType === 'prime') {
                  <i [class]="'pi ' + element.icon" class="element-icon"></i>
                }
                @if (element.iconType === 'material') {
                  <span class="material-symbols-outlined element-icon">{{ element.icon }}</span>
                }
                @if (element.type !== 'existing') {
                  <span class="element-name">{{ element.name }}</span>
                }
                @if (element.type === 'existing') {
                  <span class="element-label">{{ element.labelText || element.name }}</span>
                }
              </button>
            }
          </div>
        </div>
      
        <div class="palette-preview">
          <div class="preview-scroll">
          @if (selectedElement) {
            <div class="preview-content">
              <div class="preview-element">
                <!-- Existing Form Element Preview -->
                @if (selectedElement.type === 'existing') {
                  @if (getPreviewQuestion(); as previewQuestion) {
                    <div class="preview-existing-element">
                      <app-question-container
                        [question]="previewQuestion"
                        [isEditing]="false"
                        [disabled]="!isDropdownQuestion(previewQuestion)">
                      </app-question-container>
                    </div>
                  } @else {
                    <div class="preview-error">
                      <p>Unable to preview this form element.</p>
                      <p class="preview-error-detail">Type: {{ selectedElement.existingElementData?.type || 'unknown' }}</p>
                    </div>
                  }
                }

                <!-- Standard Element Previews -->
                @if (selectedElement.type !== 'existing') {
                  @switch (selectedElement.id) {
                    @case ('checkbox') {
                      <div class="preview-checkbox">
                        <label class="preview-label">Checkbox</label>
                        <div class="preview-checkbox-item">
                          <input type="checkbox" id="preview-cb1" disabled>
                          <label for="preview-cb1">Option 1</label>
                        </div>
                        <div class="preview-checkbox-item">
                          <input type="checkbox" id="preview-cb2" disabled>
                          <label for="preview-cb2">Option 2</label>
                        </div>
                        <div class="preview-checkbox-item">
                          <input type="checkbox" id="preview-cb3" disabled>
                          <label for="preview-cb3">Option 3</label>
                        </div>
                      </div>
                    }

                    @case ('dropdown') {
                      <div class="preview-dropdown">
                        <label class="preview-label">Dropdown</label>
                        <select disabled class="preview-select">
                          <option>Select an option</option>
                          <option>Option 1</option>
                          <option>Option 2</option>
                          <option>Option 3</option>
                        </select>
                      </div>
                    }

                    @case ('dynamic-dropdown') {
                      <div class="preview-dynamic-dropdown">
                        <label class="preview-label">Dynamic Dropdown</label>
                        <div class="preview-dropdown-trigger">
                          <span class="preview-dropdown-placeholder">Select an option</span>
                          <i class="pi pi-chevron-down preview-dropdown-arrow"></i>
                        </div>
                        <div class="preview-config-summary">
                          <span class="material-symbols-outlined preview-config-icon">dynamic_feed</span>
                          <span class="preview-config-text">Dynamic options configured</span>
                        </div>
                      </div>
                    }

                    @case ('email') {
                      <div class="preview-email">
                        <label class="preview-label">Email</label>
                        <input type="email" placeholder="example@email.com" disabled class="preview-input">
                      </div>
                    }

                    @case ('date') {
                      <div class="preview-date">
                        <label class="preview-label">Date</label>
                        <input type="date" disabled class="preview-input">
                      </div>
                    }

                    @case ('textbox') {
                      <div class="preview-textbox">
                        <label class="preview-label">Textbox</label>
                        <input type="text" placeholder="Enter text" disabled class="preview-input">
                      </div>
                    }

                    @case ('tel') {
                      <div class="preview-phone">
                        <label class="preview-label">Phone</label>
                        <input type="tel" placeholder="(555) 123-4567" disabled class="preview-input">
                      </div>
                    }

                    @case ('radio') {
                      <div class="preview-radio">
                        <label class="preview-label">Radio</label>
                        <div class="preview-radio-item">
                          <input type="radio" id="preview-radio1" name="preview-radio" disabled>
                          <label for="preview-radio1">Option 1</label>
                        </div>
                        <div class="preview-radio-item">
                          <input type="radio" id="preview-radio2" name="preview-radio" disabled>
                          <label for="preview-radio2">Option 2</label>
                        </div>
                        <div class="preview-radio-item">
                          <input type="radio" id="preview-radio3" name="preview-radio" disabled>
                          <label for="preview-radio3">Option 3</label>
                        </div>
                      </div>
                    }

                    @case ('textarea') {
                      <div class="preview-textarea">
                        <label class="preview-label">Textarea</label>
                        <textarea placeholder="Enter your message" disabled class="preview-textarea-input" rows="4"></textarea>
                      </div>
                    }

                    @case ('toggle') {
                      <div class="preview-toggle">
                        <label class="preview-label">Toggle</label>
                        <label class="preview-toggle-switch">
                          <input type="checkbox" disabled>
                          <span class="preview-toggle-slider"></span>
                        </label>
                      </div>
                    }

                    @case ('confirmation') {
                      <div class="preview-confirmation">
                        <label class="preview-label">Confirmation</label>
                        <div class="preview-confirmation-row">
                          <input type="checkbox" class="preview-confirmation-checkbox" disabled>
                          <span class="preview-confirmation-statement">
                            I confirm that the information I have given is correct
                          </span>
                        </div>
                      </div>
                    }

                    @case ('section') {
                      <div class="preview-section">
                        <div class="preview-section-divider"></div>
                        <p class="preview-section-text">Section divider</p>
                      </div>
                    }

                    @case ('title') {
                      <div class="preview-title">
                        <h5 class="preview-title-text">Title</h5>
                        <p class="preview-title-description">A heading element for organizing your form</p>
                      </div>
                    }

                    @case ('helpertext') {
                      <div class="preview-helper-text">
                        <div class="preview-helper-text-content">
                          <span class="material-symbols-outlined preview-helper-icon">help</span>
                          <p class="preview-helper-text-text">Helper text provides additional information or guidance to users</p>
                        </div>
                      </div>
                    }
                  }
                }
              </div>
            </div>
          } @else {
            <p class="preview-placeholder">Click on an element to view how it looks</p>
          }
          </div>
          @if (selectedElement) {
            <div class="preview-actions">
              @if (selectedElement && selectedElement.type !== 'existing') {
                <div class="reportable-container">
                  <label class="reportable-toggle">
                    <input
                      type="checkbox"
                      [(ngModel)]="isReportable"
                      class="reportable-checkbox">
                    <span class="reportable-label">Reportable</span>
                  </label>
                  <span class="reportable-info-text">
                    <span class="material-symbols-outlined reportable-info-icon">info</span>
                    Reportable elements are saved to the form element library for reporting purposes
                  </span>
                </div>
              }
              <button
                type="button"
                class="preview-select-button"
                (click)="confirmSelection()">
                Insert
              </button>
            </div>
          }
        </div>
      </div>
    </div>
  </div>
}

